/**
 * Camera-LOD + transfer-promoted full procedural kits — reference design
 * Reference procedural kits — Stocker3D + FabProcessEquipment.
 */
import React, { useEffect, useMemo } from 'react';
import { useAppSelector } from '../../hooks/redux';
import type { EquipmentRenderDescriptor } from './equipmentSilhouettePorts';
import { resolveKitBox } from './equipmentSilhouettePorts';
import { EquipmentKitWorldGroup } from './EquipmentKitWorldGroup';
import { CleanroomWorkcellFromSim } from './amhs/CleanroomWorkcell/CleanroomWorkcellFromSim';
import { IndustrialVerticalLifter3D } from './amhs/VerticalLifter/IndustrialVerticalLifter3D';
import { Awp300 } from './amhs/Equipment/Awp300/Awp300';
import { Stocker3D } from './amhs/Stocker/Stocker3D';
import type { LayoutStockerPortRef } from './amhs/Stocker/StockerLoadPortBank';
import type { LayoutElement } from '../../types';

export type FabEquipmentDetailLayer3DProps = {
  descriptors: EquipmentRenderDescriptor[];
  detailIds: ReadonlySet<string>;
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
  onDetailReady?: (id: string) => void;
  onDetailUnmount?: (id: string) => void;
  onSelect?: (id: string) => void;
};

type ItemProps = {
  descriptor: EquipmentRenderDescriptor;
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
  layoutElements: Record<string, LayoutElement>;
  onReady?: (id: string) => void;
  onUnmount?: (id: string) => void;
  onSelect?: (id: string) => void;
};

const FabEquipmentDetailItem: React.FC<ItemProps> = React.memo(
  ({ descriptor, layoutMeta, layoutElements, onReady, onUnmount, onSelect }) => {
    useEffect(() => {
      onReady?.(descriptor.id);
      return () => {
        onUnmount?.(descriptor.id);
      };
    }, [descriptor.id, onReady, onUnmount]);

    const box = resolveKitBox(descriptor);
    const meta = layoutMeta?.[descriptor.id] ?? layoutElements[descriptor.id];
    const cp = meta?.customProperties;
    const portCount = Math.max(1, Math.min(4, Math.round(descriptor.loadPortCount ?? 2)));
    const layoutPorts = Array.isArray(cp?.ports)
      ? (cp.ports as LayoutStockerPortRef[])
      : undefined;

    if (descriptor.kind === 'stocker') {
      const bayRaw = cp?.bayCount ?? cp?.stockerBayCount;
      return (
        <EquipmentKitWorldGroup descriptor={descriptor}>
          <Stocker3D
            id={descriptor.id}
            label={meta?.metadata?.name}
            width={box.w}
            depth={box.d}
            height={box.h}
            bayCount={typeof bayRaw === 'string' || typeof bayRaw === 'number' ? bayRaw : undefined}
            outsideStocker={Boolean(cp?.outsideStocker)}
            layoutPorts={layoutPorts}
            occupiedCount={descriptor.storedFoupsCount ?? 0}
            status="ready"
            detail="high"
            autoDetail={false}
            onSelect={() => onSelect?.(descriptor.id)}
          />
        </EquipmentKitWorldGroup>
      );
    }

    const model = (cp?.equipmentModel || cp?.model || meta?.metadata?.name || (meta as any)?.type || '').toString().toUpperCase();
    const rawType = ((meta as any)?.type || cp?.objectType || cp?.equipmentType || '').toString().toUpperCase();
    const isLifter = descriptor.kind === 'equipment' && (
      model.includes('LIFTER') ||
      model.includes('VTU') ||
      model.includes('ELEVATOR') ||
      model.includes('LIFT') ||
      rawType.includes('LIFTER') ||
      rawType.includes('LIFT') ||
      rawType === 'STB' ||
      rawType === 'OHB' ||
      cp?.equipmentType === 'LIFTER' ||
      cp?.equipmentType === 'VERTICAL_LIFTER'
    );
    const isAwp300 = model.includes('AWP') || model === 'EXISTING' || cp?.equipmentType === 'EXISTING' || cp?.equipmentType === 'AWP300';
    const isWorkcell = model.includes('WORKCELL') || model.includes('ROBOT') || model.includes('INSPECT') || cp?.equipmentType === 'CLEANROOM_WORKCELL';

    if (isLifter) {
      return (
        <EquipmentKitWorldGroup descriptor={descriptor}>
          <IndustrialVerticalLifter3D
            id={descriptor.id}
            height={box.h}
            towerWidth={Math.max(3.8, Math.min(box.w, box.d) * 0.45)}
            towerDepth={Math.max(3.8, Math.min(box.w, box.d) * 0.45)}
            conveyorLength={Math.max(6.0, Math.max(box.w, box.d) * 0.65)}
            opacity={1}
            onSelect={() => onSelect?.(descriptor.id)}
          />
        </EquipmentKitWorldGroup>
      );
    }

    if (isWorkcell) {
      return (
        <EquipmentKitWorldGroup descriptor={descriptor}>
          <CleanroomWorkcellFromSim
            element={meta as LayoutElement}
            id={descriptor.id}
            width={box.w}
            depth={box.d}
            height={box.h}
            portFractions={descriptor.portFractions}
            opacity={1}
            onSelect={() => onSelect?.(descriptor.id)}
          />
        </EquipmentKitWorldGroup>
      );
    }

    if (isAwp300) {
      return (
        <EquipmentKitWorldGroup descriptor={descriptor}>
          <Awp300
            id={descriptor.id}
            width={box.w}
            depth={box.d}
            height={box.h}
            ports={portCount}
            portFractions={descriptor.portFractions}
            status="IDLE"
            autoDetail={false}
            opacity={1}
            onSelect={() => onSelect?.(descriptor.id)}
          />
        </EquipmentKitWorldGroup>
      );
    }

    return (
      <EquipmentKitWorldGroup descriptor={descriptor}>
        <CleanroomWorkcellFromSim
          element={meta as LayoutElement}
          id={descriptor.id}
          width={box.w}
          depth={box.d}
          height={box.h}
          opacity={1}
          onSelect={() => onSelect?.(descriptor.id)}
        />
      </EquipmentKitWorldGroup>
    );
  }
);

FabEquipmentDetailItem.displayName = 'FabEquipmentDetailItem';

export const FabEquipmentDetailLayer3D: React.FC<FabEquipmentDetailLayer3DProps> = React.memo(
  ({ descriptors, detailIds, layoutMeta, onDetailReady, onDetailUnmount, onSelect }) => {
    const layoutElements = useAppSelector((s) => s.layout.elements);

    const detailed = useMemo(
      () => descriptors.filter((d) => detailIds.has(d.id)),
      [detailIds, descriptors]
    );

    if (detailed.length === 0) return null;

    return (
      <group name="FabEquipmentDetailLayer3D">
        {detailed.map((descriptor) => (
          <FabEquipmentDetailItem
            key={descriptor.id}
            descriptor={descriptor}
            layoutMeta={layoutMeta}
            layoutElements={layoutElements}
            onReady={onDetailReady}
            onUnmount={onDetailUnmount}
            onSelect={onSelect}
          />
        ))}
      </group>
    );
  }
);

FabEquipmentDetailLayer3D.displayName = 'FabEquipmentDetailLayer3D';

export default FabEquipmentDetailLayer3D;
